import React from 'react'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import TagPreview from './TagPreview'

const LEFT_FIELDS = [
  { key: 'barcode', label: 'Barcode' },
  { key: 'barcodeNumber', label: 'Barcode Number' },
  { key: 'sku', label: 'SKU' },
  { key: 'karat', label: 'Karat' }
]

const RIGHT_FIELDS = [
  { key: 'title', label: 'Title' },
  { key: 'productType', label: 'Product Type' },
  { key: 'weight', label: 'Weight' },
  { key: 'price', label: 'Price' }
]

const LEFT_FONT_FIELDS = [
  { key: 'sku', label: 'SKU / Barcode #' },
  { key: 'karat', label: 'Karat' }
]

const RIGHT_FONT_FIELDS = [
  { key: 'title', label: 'Title' },
  { key: 'productType', label: 'Product Type' },
  { key: 'weight', label: 'Weight' },
  { key: 'price', label: 'Price' }
]

const TagConfigSidebar = ({
  tagConfig,
  onUpdateConfig,
  selectedProduct,
  tagPrintInfo = '',
  onTagPrintInfoChange,
  tagPrintInfoMax = 24,
  onOpenPrintPreview,
  onSaveSettings,
  saveBusy = false
}) => {
  const cfg = tagConfig || {}
  const fields = cfg.fields || {}
  const fontSize = cfg.fontSize || {}
  const barcodeCfg = cfg.barcode || {}

  const toggleField = (key) => {
    onUpdateConfig({
      fields: { [key]: !fields[key] }
    })
  }

  const updateFontSize = (key, value) => {
    const num = parseFloat(value)
    if (!Number.isFinite(num) || num < 3 || num > 20) return
    onUpdateConfig({
      fontSize: { [key]: num }
    })
  }

  const updateBarcodeOption = (key, value) => {
    onUpdateConfig({
      barcode: { [key]: value }
    })
  }

  const updateAlignment = (side, value) => {
    onUpdateConfig({
      alignment: { [side]: value }
    })
  }

  const AlignButton = ({ side, value, icon }) => {
    const current = cfg.alignment?.[side] || (side === 'left' ? 'left' : 'right')
    const isActive = current === value
    return (
      <button
        type="button"
        onClick={() => updateAlignment(side, value)}
        className={`p-1.5 rounded transition-colors ${
          isActive
            ? 'bg-primary/10 text-primary border border-primary/30'
            : 'text-muted-foreground hover:bg-muted/50 border border-transparent'
        }`}
        title={`Align ${value}`}
      >
        <Icon name={icon} size={14} />
      </button>
    )
  }

  return (
    <Card variant="solid" className="border border-border/60 shadow-elevation-1">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Settings" size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">Label Setting</h3>
        </div>
        {onSaveSettings && (
          <Button
            variant="default"
            size="sm"
            onClick={onSaveSettings}
            disabled={saveBusy}
          >
            <Icon name={saveBusy ? "Loader2" : "Save"} size={14} className={`mr-1 ${saveBusy ? 'animate-spin' : ''}`} />
            {saveBusy ? 'Saving...' : 'Save'}
          </Button>
        )}
      </div>

      {/* Live Preview */}
      <div className="mb-4 pb-4 border-b border-border/60">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
          Reference Preview
        </label>
        <div className="overflow-x-auto">
          <TagPreview tagConfig={tagConfig} product={selectedProduct} />
        </div>
        {onOpenPrintPreview && (
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenPrintPreview}
            className="mt-3"
          >
            <Icon name="Printer" size={14} className="mr-1" />
            Open Real Print Preview
          </Button>
        )}
      </div>

      {/* Tag Print Info */}
      <div className="mb-4 pb-4 border-b border-border/60">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Tag Print Info
          </label>
          <span className="text-[11px] text-muted-foreground">
            {String(tagPrintInfo || '').length}/{tagPrintInfoMax}
          </span>
        </div>
        <input
          type="text"
          value={tagPrintInfo}
          onChange={(e) => onTagPrintInfoChange?.(String(e.target.value || '').slice(0, tagPrintInfoMax))}
          maxLength={tagPrintInfoMax}
          placeholder="AU14K RING CZ1.2CT 3.5G"
          className="w-full px-3 py-2 bg-input text-foreground placeholder:text-muted-foreground border border-border/60 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Short print line with max {tagPrintInfoMax} characters.
        </p>
      </div>

      {/* Side Adjustments */}
      <div className="mb-4 pb-4 border-b border-border/60">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
          Side Adjustments
        </label>

        <div className="space-y-3">
          <div className="rounded-xl border border-border/60 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Left Side Adjustments</span>
              <div className="flex gap-1">
                <AlignButton side="left" value="left" icon="AlignLeft" />
                <AlignButton side="left" value="center" icon="AlignCenter" />
                <AlignButton side="left" value="right" icon="AlignRight" />
              </div>
            </div>

            <div className="space-y-1.5 mb-3">
              {LEFT_FIELDS.map(({ key, label }) => (
                <label key={key} className="flex items-center justify-between cursor-pointer group">
                  <span className="text-sm text-foreground group-hover:text-primary transition-colors">{label}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!fields[key]}
                    onClick={() => toggleField(key)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      fields[key] ? 'bg-primary' : 'bg-muted/60'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        fields[key] ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </label>
              ))}
            </div>

            <div className="space-y-2">
              {LEFT_FONT_FIELDS.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">{label}</span>
                  <input
                    type="number"
                    step="0.5"
                    min="3"
                    max="20"
                    value={fontSize[key] || 6}
                    onChange={(e) => updateFontSize(key, e.target.value)}
                    className="w-16 px-2 py-1 border border-border/60 rounded bg-input text-foreground text-xs text-center focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border/60 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Right Side Adjustments</span>
              <div className="flex gap-1">
                <AlignButton side="right" value="left" icon="AlignLeft" />
                <AlignButton side="right" value="center" icon="AlignCenter" />
                <AlignButton side="right" value="right" icon="AlignRight" />
              </div>
            </div>

            <div className="space-y-1.5 mb-3">
              {RIGHT_FIELDS.map(({ key, label }) => (
                <label key={key} className="flex items-center justify-between cursor-pointer group">
                  <span className="text-sm text-foreground group-hover:text-primary transition-colors">{label}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!fields[key]}
                    onClick={() => toggleField(key)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      fields[key] ? 'bg-primary' : 'bg-muted/60'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        fields[key] ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </label>
              ))}
            </div>

            <div className="space-y-2">
              {RIGHT_FONT_FIELDS.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">{label}</span>
                  <input
                    type="number"
                    step="0.5"
                    min="3"
                    max="20"
                    value={fontSize[key] || 6}
                    onChange={(e) => updateFontSize(key, e.target.value)}
                    className="w-16 px-2 py-1 border border-border/60 rounded bg-input text-foreground text-xs text-center focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Barcode Options */}
      <div>
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
          Barcode Options (Left Side)
        </label>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Format</span>
            <select
              value={barcodeCfg.format || 'EAN8'}
              onChange={(e) => updateBarcodeOption('format', e.target.value)}
              className="px-2 py-1 border border-border/60 rounded bg-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
            >
              <option value="EAN8">EAN-8</option>
              <option value="CODE128">Code 128</option>
              <option value="CODE39">Code 39</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Bar Width</span>
            <input
              type="number"
              step="0.1"
              min="0.3"
              max="2"
              value={barcodeCfg.width || 0.7}
              onChange={(e) => updateBarcodeOption('width', parseFloat(e.target.value))}
              className="w-16 px-2 py-1 border border-border/60 rounded bg-input text-foreground text-xs text-center focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Bar Height</span>
            <input
              type="number"
              step="1"
              min="10"
              max="40"
              value={barcodeCfg.height || 22}
              onChange={(e) => updateBarcodeOption('height', parseInt(e.target.value, 10))}
              className="w-16 px-2 py-1 border border-border/60 rounded bg-input text-foreground text-xs text-center focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
          </div>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-xs text-muted-foreground">Show Value Below</span>
            <button
              type="button"
              role="switch"
              aria-checked={!!barcodeCfg.displayValue}
              onClick={() => updateBarcodeOption('displayValue', !barcodeCfg.displayValue)}
              className={`relative w-9 h-5 rounded-full transition-colors ${
                barcodeCfg.displayValue ? 'bg-primary' : 'bg-muted/60'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  barcodeCfg.displayValue ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </label>
        </div>
      </div>
    </Card>
  )
}

export default TagConfigSidebar
